/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from "react";
import { api, spriteUrl } from "@/lib/api";
import { X, Sparkles } from "lucide-react";

// Cache pokedex descriptions in memory
const DESC_CACHE = new Map();
async function fetchPokedexDesc(pokemonId) {
  if (DESC_CACHE.has(pokemonId)) return DESC_CACHE.get(pokemonId);
  try {
    const res = await fetch(`https://pokeapi.co/api/v2/pokemon-species/${pokemonId}`);
    if (!res.ok) throw new Error("PokeAPI");
    const data = await res.json();
    const frEntry = data.flavor_text_entries?.find((e) => e.language?.name === "fr");
    const enEntry = data.flavor_text_entries?.find((e) => e.language?.name === "en");
    const raw = (frEntry || enEntry)?.flavor_text || "";
    const cleaned = raw.replace(/[\f\n\r\u000c]/g, " ").trim();
    DESC_CACHE.set(pokemonId, cleaned);
    return cleaned;
  } catch {
    DESC_CACHE.set(pokemonId, "");
    return "";
  }
}

export default function PokedexPage() {
  const [dex, setDex] = useState({ sections: [], seen: 0, caught: 0, total: 0 });
  const [detail, setDetail] = useState(null); // { pokemon_id, name, ... }

  const load = useCallback(async () => {
    try { const { data } = await api.get("/pokedex"); setDex(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div data-testid="pokedex-page" className="section-header">
      <div className="flex justify-between items-end mb-4 flex-wrap gap-3">
        <div>
          <div className="overline">Catalogue des espèces · Observations naturalistes</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-1">Pokédex</h1>
        </div>
        <div className="flex items-center gap-4">
          <Stat label="Vus" value={dex.seen} testid="dex-seen"/>
          <Stat label="Capturés" value={dex.caught} testid="dex-caught"/>
          <Stat label="Total" value={dex.total} testid="dex-total"/>
        </div>
      </div>

      <div className="space-y-6">
        {dex.sections?.map((sec) => (
          <section key={sec.region_id} data-testid={`pokedex-section-${sec.region_id}`}>
            <div className="flex justify-between items-baseline mb-2 border-b border-[#2B2B2B] pb-1">
              <h2 className="font-serif-display text-2xl">{sec.region_name}</h2>
              <span className="font-mono text-xs text-[#4A4A4A]">
                #{sec.range[0].toString().padStart(3,"0")}-{sec.range[1].toString().padStart(3,"0")} · vus {sec.seen} · capturés {sec.caught}/{sec.total}
              </span>
            </div>
            <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-9 lg:grid-cols-11 xl:grid-cols-13 gap-[1px] bg-[#2B2B2B] border border-[#2B2B2B]">
              {sec.entries.map((e) => (
                <PokedexCell
                  key={e.pokemon_id}
                  entry={e}
                  onClick={() => e.status !== "unknown" && setDetail(e)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {detail && <PokedexDetailModal entry={detail} onClose={() => setDetail(null)}/>}
    </div>
  );
}

function PokedexCell({ entry, onClick }) {
  const status = entry.status;
  const isShiny = entry.is_shiny;
  const filter = status === "caught" ? "none" : status === "seen" ? "grayscale(100%) brightness(0%)" : "none";
  const opacity = status === "unknown" ? 0.1 : 1;
  const spriteSrc = isShiny && status === "caught"
    ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${entry.pokemon_id}.png`
    : spriteUrl(entry.pokemon_id);
  const clickable = status !== "unknown";
  return (
    <div
      className={`aspect-square flex flex-col items-center justify-center p-1 relative ${isShiny ? "bg-[#FFF4D9]" : "bg-[#FCF9F2]"} ${clickable ? "cursor-pointer hover:brightness-95" : ""}`}
      title={status === "unknown" ? `#${entry.pokemon_id.toString().padStart(3,"0")} — ???` : `#${entry.pokemon_id.toString().padStart(3,"0")} — ${entry.name}${isShiny ? " ✨ chromatique" : ""}`}
      data-testid={`dex-cell-${entry.pokemon_id}`}
      onClick={onClick}
    >
      <span className="absolute top-0.5 left-1 text-[8px] font-mono text-[#7A7A7A]">
        {entry.pokemon_id.toString().padStart(3, "0")}
      </span>
      {status === "unknown" ? (
        <img
          src={spriteUrl(entry.pokemon_id)}
          alt=""
          aria-hidden="true"
          className="silhouette w-full h-full object-contain"
        />
      ) : (
        <img
          src={spriteSrc}
          alt={entry.name}
          className="pixelated w-full h-full object-contain"
          style={{ filter, opacity }}
        />
      )}
      {status === "caught" && (
        <span className={`stamp-validated ${isShiny ? "stamp-shiny" : ""}`} aria-hidden="true">
          {isShiny ? "Shiny" : "Vu"}
        </span>
      )}
      {isShiny && (
        <span className="absolute top-0 left-0.5 text-[10px] text-[#B87333]" data-testid={`dex-shiny-${entry.pokemon_id}`}>✦</span>
      )}
    </div>
  );
}

function PokedexDetailModal({ entry, onClose }) {
  const [detail, setDetail] = useState(null);
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [dResp, d] = await Promise.all([
          api.get(`/pokedex/${entry.pokemon_id}`),
          fetchPokedexDesc(entry.pokemon_id),
        ]);
        if (cancelled) return;
        setDetail(dResp.data);
        setDesc(d);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [entry.pokemon_id]);

  const spriteSrc = entry.is_shiny
    ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/shiny/${entry.pokemon_id}.png`
    : `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${entry.pokemon_id}.png`;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 overflow-auto"
      style={{ background: "rgba(26,26,26,0.6)", zIndex: 80 }}
      onClick={onClose}
      data-testid="pokedex-detail-modal"
    >
      <div className="panel-strong p-0 max-w-md w-full my-8 relative" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="pokedex-detail-close"
          aria-label="Fermer la fiche"
          className="absolute top-2 right-2 z-20 w-11 h-11 rounded-full bg-[#1A1A1A] text-[#F4EFE6] border-2 border-[#F4EFE6] flex items-center justify-center shadow-lg hover:bg-[#8C2A2A] transition-colors"
          onClick={onClose}
        >
          <X size={22} strokeWidth={2.5}/>
        </button>

        <header className={`p-4 border-b border-[#2B2B2B] ${entry.is_shiny ? "bg-[#FFF4D9]" : "bg-[#EAE4D9]"} flex flex-col items-center`}>
          <img
            src={spriteSrc}
            alt={entry.name}
            className="w-40 h-40 object-contain drop-shadow-md"
            data-testid="pokedex-detail-sprite"
          />
          <div className="flex items-center gap-2 mt-2">
            <span className="font-mono text-[11px] text-[#7A7A7A]">
              #{entry.pokemon_id.toString().padStart(3, "0")}
            </span>
            {entry.is_shiny && (
              <span className="font-mono text-[10px] border border-[#B87333] bg-[#FCF9F2] text-[#B87333] px-1.5 py-0.5 inline-flex items-center gap-1">
                <Sparkles size={10}/> Chromatique
              </span>
            )}
          </div>
          <h2 className="font-serif-display text-3xl leading-none mt-1" data-testid="pokedex-detail-name">
            {entry.name}
          </h2>
        </header>

        <div className="p-4 space-y-3">
          <div data-testid="pokedex-detail-desc">
            <div className="overline mb-1">Notice du Pokédex</div>
            <p className={`text-sm italic ${desc ? "text-[#1A1A1A]" : "text-[#7A7A7A]"}`}>
              {loading ? "Chargement…" : (desc || "Aucune notice disponible.")}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2" data-testid="pokedex-detail-stats">
            <StatCard label="Capturés (sauvage)" value={detail?.wild_captures ?? "—"} color="#2E4F32"/>
            <StatCard label="Obtenus par évolution" value={detail?.evolution_count ?? "—"} color="#B87333"/>
            {detail?.legendary_captures > 0 && (
              <StatCard label="Capture légendaire" value={detail.legendary_captures} color="#B87333"/>
            )}
            {detail?.starter_count > 0 && (
              <StatCard label="Choisi comme starter" value={detail.starter_count} color="#2E4F32"/>
            )}
          </div>

          <div className="pt-2 border-t border-[#D8D2C4]">
            <div className="overline mb-1">Actuellement dans le labo</div>
            <div className="flex gap-3 font-mono text-xs">
              <span className="text-[#4A4A4A]">Équipes : <strong className="text-[#1A1A1A]">{detail?.team_owned ?? 0}</strong></span>
              <span className="text-[#4A4A4A]">PC : <strong className="text-[#1A1A1A]">{detail?.pc_owned ?? 0}</strong></span>
              <span className="text-[#4A4A4A]">Total : <strong className="text-[#1A1A1A]">{detail?.currently_owned ?? 0}</strong></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="panel p-2 text-center">
      <div className="overline text-[9px]">{label}</div>
      <div className="font-serif-display text-2xl leading-none mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="panel px-3 py-1.5" data-testid={testid}>
      <div className="overline text-[9px]">{label}</div>
      <div className="font-serif-display text-2xl leading-none">{value}</div>
    </div>
  );
}
